/* =========================================================
   Affiche AVIRON — le bateau, le 2 000 m, l'ergomètre, l'équipage, la saison.
   API : SNAvBateau.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, joueur, equipe, bateau,
              mode 'bateau'   → s1..s3 (« valeur|libellé ») ; sinon les cotes du bateau
              mode 'course'   → temps, place, c1..c4 (« 1:38.2 ») : les passages aux 500
              mode 'ergo'     → t1..t6 (« Valeur|Libellé »)
              mode 'equipage' → e1..e9 (« Siège 8|Thomas Leroux »)
              mode 'saison'   → l1..l8 (« Libellé|valeur ») }
     opts : { palette }

   Le type de bateau passe par une TABLE FERMÉE : il pilote le nombre de sièges,
   la nage en couple ou en pointe et la présence d'un barreur. Une valeur
   inconnue retombe sur le skiff.

   Auto-suffisant (aucun fetch, aucune image, aucun aléa) → re-render serveur.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    riviere: { label:'Rivière', bg:'#0F2A33', ink:'#F1F2EC', muted:'#87A3A9', accent:'#E8A33D', line:'#1D4550', card:'#16363F' },
    craie:   { label:'Craie',   bg:'#F7F4ED', ink:'#0F2A33', muted:'#7C8794', accent:'#C2410C', line:'#E3DDD0', card:'#FFFFFF' },
    aube:    { label:'Aube',    bg:'#2B1F2C', ink:'#F4EEF2', muted:'#AE96AC', accent:'#E8A33D', line:'#443247', card:'#382A3A' },
    nuit:    { label:'Nuit',    bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  /* « 1:38.2 », « 6:12,4 » ou « 98,2 » → secondes. Null si illisible. */
  function chrono(s) {
    const t = String(s == null ? '' : s).trim().replace(',', '.');
    if (!t) return null;
    const m = t.match(/^(?:(\d+):)?(\d+(?:\.\d+)?)$/);
    if (!m) return null;
    const v = (m[1] ? parseInt(m[1], 10) * 60 : 0) + parseFloat(m[2]);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  /* Poids MINIMAUX de la fédération internationale — ceux-là sont réglementés.
     Les longueurs ne le sont pas : elles ne figurent donc pas ici plutôt que
     d'annoncer une cote officielle qui n'existe pas. */
  const BATEAUX = {
    '1x': { n: 1, couple: true,  label: 'Skiff',               poids: '14 kg' },
    '2x': { n: 2, couple: true,  label: 'Deux de couple',      poids: '27 kg' },
    '2-': { n: 2, couple: false, label: 'Deux sans barreur',   poids: '27 kg' },
    '4x': { n: 4, couple: true,  label: 'Quatre de couple',    poids: '52 kg' },
    '4-': { n: 4, couple: false, label: 'Quatre sans barreur', poids: '50 kg' },
    '4+': { n: 4, couple: false, barreur: true, label: 'Quatre barré', poids: '51 kg' },
    '8+': { n: 8, couple: false, barreur: true, label: 'Huit barré',   poids: '96 kg' },
  };
  const litBateau = v => BATEAUX[String(v == null ? '' : v).trim().toLowerCase().replace(/\s/g, '')] || BATEAUX['1x'];

  /* ---------------------------------------------------------------
     LE BATEAU, vu de dessus. La coque est une lentille pointée aux deux bouts ;
     les sièges s'échelonnent sur la ligne d'axe, chacun avec sa portique et
     son ou ses avirons. En COUPLE le rameur tient deux avirons, un de chaque
     côté ; en POINTE il n'en tient qu'un, et les côtés alternent d'un siège à
     l'autre — c'est ce détail qui distingue un quatre de couple d'un quatre
     sans barreur au premier regard.
     --------------------------------------------------------------- */
  function bateau(cx, cy, H, B, p) {
    const Y0 = cy - H / 2, w = Math.max(13, H * 0.028);
    const L = p.ink;
    let g = `<path d="M ${cx} ${Y0.toFixed(1)}`
      + ` C ${(cx + w)} ${(Y0 + H*0.22).toFixed(1)} ${(cx + w)} ${(Y0 + H*0.78).toFixed(1)} ${cx} ${(Y0 + H).toFixed(1)}`
      + ` C ${(cx - w)} ${(Y0 + H*0.78).toFixed(1)} ${(cx - w)} ${(Y0 + H*0.22).toFixed(1)} ${cx} ${Y0.toFixed(1)} Z"`
      + ` fill="${p.card}" stroke="${L}" stroke-width="3"/>`;

    /* Les sièges occupent la partie centrale de la coque, jamais les pointes. */
    const haut = Y0 + H * 0.20, bas = Y0 + H * (B.barreur ? 0.80 : 0.84);
    const pas = B.n > 1 ? (bas - haut) / (B.n - 1) : 0;
    const lgAviron = Math.min(150, H * 0.24), lgPelle = lgAviron * 0.30;

    for (let i = 0; i < B.n; i++) {
      const y = B.n > 1 ? haut + i * pas : (haut + bas) / 2;
      /* en pointe, le côté alterne d'un siège au suivant */
      const cotes = B.couple ? [-1, 1] : [i % 2 === 0 ? -1 : 1];
      for (const c of cotes) {
        const x0 = cx + c * w * 0.7, x1 = cx + c * lgAviron;
        g += `<line x1="${x0.toFixed(1)}" y1="${y.toFixed(1)}" x2="${x1.toFixed(1)}" y2="${(y + 16).toFixed(1)}" stroke="${L}" stroke-width="3" stroke-linecap="round"/>`
          + `<rect x="${(c < 0 ? x1 - lgPelle : x1).toFixed(1)}" y="${(y + 10).toFixed(1)}" width="${lgPelle.toFixed(1)}" height="13" rx="4" fill="${p.accent}"/>`;
      }
      g += `<rect x="${(cx - w*0.52).toFixed(1)}" y="${(y - 7).toFixed(1)}" width="${(w*1.04).toFixed(1)}" height="14" rx="6" fill="${L}"/>`;
    }
    /* le barreur, à la poupe : pas de siège coulissant, un simple poste */
    if (B.barreur) g += `<circle cx="${cx}" cy="${(Y0 + H*0.90).toFixed(1)}" r="${(w*0.55).toFixed(1)}" fill="none" stroke="${L}" stroke-width="3"/>`;
    return g;
  }

  function stats(p, items, y) {
    const it = (items || []).filter(x => x && String(x[0] || '').trim());
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }
  function troisValeurs(p, data, y, defaut) {
    const perso = [1, 2, 3].map(i => {
      const raw = String(data['s' + i] || '').trim(); if (!raw) return null;
      const [v, l] = raw.split('|').map(s => (s || '').trim());
      return v ? [v, l || ''] : null;
    });
    return stats(p, perso.some(Boolean) ? perso.filter(Boolean) : (defaut || []), y);
  }

  /* Filigrane : une pelle d'aviron, manche compris. */
  function filigrane(p) {
    const cx = 400, y0 = 300, h = 470, w = 140;
    return `<g opacity="0.055"><rect x="${cx - 10}" y="${y0}" width="20" height="${(h * 0.44).toFixed(1)}" rx="10" fill="${p.ink}"/>`
      + `<path d="M ${cx} ${(y0 + h * 0.40).toFixed(1)} C ${(cx + w * 0.62).toFixed(1)} ${(y0 + h * 0.44).toFixed(1)} ${(cx + w * 0.72).toFixed(1)} ${(y0 + h * 0.52).toFixed(1)} ${(cx + w * 0.72).toFixed(1)} ${(y0 + h * 0.64).toFixed(1)}`
      + ` L ${(cx + w * 0.72).toFixed(1)} ${(y0 + h * 0.93).toFixed(1)} Q ${(cx + w * 0.72).toFixed(1)} ${(y0 + h).toFixed(1)} ${(cx + w * 0.52).toFixed(1)} ${(y0 + h).toFixed(1)}`
      + ` L ${(cx - w * 0.60).toFixed(1)} ${(y0 + h * 0.95).toFixed(1)} Q ${(cx - w * 0.72).toFixed(1)} ${(y0 + h * 0.93).toFixed(1)} ${(cx - w * 0.72).toFixed(1)} ${(y0 + h * 0.83).toFixed(1)}`
      + ` L ${(cx - w * 0.72).toFixed(1)} ${(y0 + h * 0.62).toFixed(1)} C ${(cx - w * 0.72).toFixed(1)} ${(y0 + h * 0.50).toFixed(1)} ${(cx - w * 0.62).toFixed(1)} ${(y0 + h * 0.44).toFixed(1)} ${cx} ${(y0 + h * 0.40).toFixed(1)} Z" fill="${p.ink}"/></g>`;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.riviere;
    const mode = String(data.mode || 'bateau').toLowerCase();
    const cx = PW / 2;
    const B = litBateau(data.bateau);

    const DEF = { bateau: 'LE BATEAU', course: 'LE 2 000 M', ergo: "L'ERGOMÈTRE", equipage: "L'ÉQUIPAGE", saison: 'NOTRE SAISON' };
    const titre = up(data.titre) || DEF[mode] || 'LE BATEAU';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || 'AVIRON';
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = 0;

    if (mode === 'bateau') {
      corps = bateau(cx, 578, 620, B, p)
        + T(cx, 918, 11.5, p.muted, up(B.label) + ' · ' + (B.couple ? 'NAGE EN COUPLE' : 'NAGE EN POINTE'), { w:600, ls:2.8 });
      barre = troisValeurs(p, data, 968, [['2 000 m', 'Distance'], [String(B.n), B.n > 1 ? 'Rameurs' : 'Rameur'], [B.poids, 'Poids minimum']]);
      pied = 1030;

    } else if (mode === 'course') {
      corps = `<rect x="${MX}" y="268" width="${PW-2*MX}" height="128" rx="14" fill="${p.ink}"/>`
        + T(cx, 308, 13, p.accent, esc(up(data.place)) || up(B.label), { w:700, ls:6 })
        + T(cx, 368, fit(esc(up(data.temps)) || '—', 560, 60, 0.56, 0, 28), p.bg, esc(up(data.temps)) || '—', { f:'Oswald', w:700 });

      /* LES QUATRE PASSAGES AUX 500. Barres proportionnelles au temps : sur
         2 000 m le profil classique — départ rapide, creux au troisième 500,
         relance finale — se voit d'un coup d'œil, ce qu'une simple liste de
         chiffres ne montre pas. */
      const c = [1, 2, 3, 4].map(i => chrono(data['c' + i]));
      if (c.some(Boolean)) {
        const lus = c.filter(v => v != null);
        const lent = Math.max.apply(null, lus), rapide = Math.min.apply(null, lus);
        const X0 = MX + 96, X1 = PW - MX - 96, y0 = 486, pas = 96;
        c.forEach((v, i) => {
          const y = y0 + i * pas;
          corps += T(MX, y + 6, 14, p.muted, ((i + 1) * 500) + ' M', { w:600, ls:2, a:'start' });
          if (v == null) { corps += T(X0, y + 6, 15, p.muted, '—', { f:'Oswald', w:700, a:'start' }); return; }
          const part = lent === rapide ? 1 : 0.42 + 0.58 * (v - rapide) / (lent - rapide);
          corps += `<rect x="${X0}" y="${(y - 13).toFixed(1)}" width="${((X1 - X0) * part).toFixed(1)}" height="26" rx="6" fill="${v === rapide ? p.accent : p.card}"/>`
            + T(PW - MX, y + 7, 24, p.ink, esc(String(data['c' + (i + 1)] || '').trim()), { f:'Oswald', w:700, a:'end' });
        });
      } else corps += T(cx, 620, 20, p.muted, 'VOS QUATRE PASSAGES AUX 500 M : 1:38.2', { w:600, ls:2 });
      barre = troisValeurs(p, data, 962, null);
      pied = 1030;

    } else if (mode === 'ergo') {
      const bl = [];
      for (let i = 1; i <= 6; i++) {
        const raw = String(data['t' + i] || '').trim(); if (!raw) continue;
        const [v, l] = raw.split('|').map(s => (s || '').trim());
        if (v && l) bl.push([v, l]);
      }
      if (bl.length) {
        const cols = bl.length <= 4 ? 2 : 3, rangs = Math.ceil(bl.length / cols);
        const bw = (PW - 2*MX - (cols-1)*16) / cols, bh = Math.min(180, 560 / rangs);
        bl.forEach((b, i) => {
          const c = i % cols, r = Math.floor(i / cols);
          const x = MX + c * (bw + 16), y = 340 + r * (bh + 16);
          corps += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${bh.toFixed(1)}" rx="16" fill="${p.card}"/>`
            + T(x + bw/2, y + bh*0.60, fit(b[0], bw - 28, 58, 0.56, 0, 22), p.ink, esc(b[0]), { f:'Oswald', w:700 })
            + T(x + bw/2, y + bh*0.82, 13, p.muted, esc(up(b[1])), { w:600, ls:2 });
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LA VALEUR, UNE BARRE VERTICALE, SON LIBELLÉ', { w:600, ls:1.5 });
      barre = troisValeurs(p, data, 976, null);
      pied = 1032;

    } else if (mode === 'equipage') {
      const l = [];
      for (let i = 1; i <= 9; i++) {
        const raw = String(data['e' + i] || '').trim(); if (!raw) continue;
        const [poste, nom] = raw.split('|').map(s => (s || '').trim());
        if (poste || nom) l.push([poste, nom]);
      }
      if (l.length) {
        const zone = 640, pas = Math.min(80, zone / l.length), y0 = 336 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas;
          corps += T(MX, y + 4, 14, p.muted, esc(up(r[0])), { w:600, ls:2.5, a:'start' })
            + T(PW - MX, y + 8, fit(r[1], 470, 30, 0.58, 0, 15), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 4).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 4).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LE POSTE, UNE BARRE VERTICALE, LE NOM', { w:600, ls:1.5 });
      pied = 1010;

    } else { /* saison */
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
        const [lab, v] = raw.split('|').map(s => (s || '').trim());
        if (lab && v) l.push([lab, v]);
      }
      if (l.length) {
        const zone = 600, pas = Math.min(96, zone / l.length), y0 = 308 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas, dernier = i === l.length - 1;
          corps += T(MX, y, 17, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' })
            + T(PW - MX, y + 8, fit(r[1], 420, 44, 0.58, 0, 20), dernier ? p.accent : p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (!dernier) corps += `<line x1="${MX}" y1="${(y + pas/2 - 14).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 14).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LIBELLÉ, BARRE VERTICALE, VALEUR', { w:600, ls:2 });
      pied = 976;
    }

    /* Le filigrane n'est posé que sur les modes où la place est vraiment
       vide : là où un dessin occupe déjà l'affiche, il ne ferait que salir. */
    const fili = ["course","equipage","saison"].indexOf(mode) >= 0 ? filigrane(p) : '';
    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${fili}
${corps}
${barre}
${pied && bas ? T(cx, pied, fit(bas, 690, 19, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNAvBateau = { render, PALETTES, BATEAUX };
})();
